import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  Loader2, Check, AlertTriangle, Star, ChevronDown,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ─── Public form (no auth) ─────────────────────────────────────────
// Anyone with the share URL `/f/:token` can fill out the form.
// We collect the visitor's name/email/phone up front so the salon
// owner gets a CRM-linked submission, then render every field type
// the editor supports.
export default function PublicFormPage() {
  const { token } = useParams();
  const [stage, setStage] = useState("loading"); // loading | form | done | error
  const [form, setForm] = useState(null);
  const [errMsg, setErrMsg] = useState("");
  const [contact, setContact] = useState({ first_name: "", last_name: "", email: "", phone: "" });
  const [responses, setResponses] = useState({});
  const [signature, setSignature] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [validation, setValidation] = useState({});

  // Keep page title in sync with the form name so iOS / browser tabs
  // show something meaningful when the link is shared.
  useEffect(() => {
    if (form?.name) document.title = `${form.name} · SalonFlow`;
  }, [form]);

  useEffect(() => {
    if (!token) { setStage("error"); setErrMsg("Invalid form link"); return; }
    let cancelled = false;
    const load = async () => {
      try {
        const r = await axios.get(`${API}/public/forms/${token}`);
        if (cancelled) return;
        // Seed default values from the form's field definitions so they
        // render pre-filled exactly like a salon would expect.
        const defaults = {};
        for (const f of r.data.fields || []) {
          if (f.default_value !== undefined && f.default_value !== "") {
            defaults[f.id] = f.default_value;
          }
        }
        setForm(r.data);
        if (Object.keys(defaults).length) setResponses(defaults);
        setStage("form");
      } catch (err) {
        if (cancelled) return;
        setErrMsg(err.response?.status === 404
          ? "This form is no longer available."
          : "Couldn't load the form. Please try again.");
        setStage("error");
      }
    };
    load();
    return () => { cancelled = true; };
  }, [token]);

  const setResponse = (fieldId, value) =>
    setResponses(prev => ({ ...prev, [fieldId]: value }));

  const validate = () => {
    const errs = {};
    if (!contact.first_name.trim()) errs.__first_name = "Required";
    for (const f of form.fields || []) {
      if (!f.required) continue;
      if (["heading", "paragraph", "divider"].includes(f.type)) continue;
      const v = responses[f.id];
      if (v === undefined || v === null || v === "" ||
          (Array.isArray(v) && v.length === 0)) {
        errs[f.id] = "Required";
      }
    }
    setValidation(errs);
    return errs;
  };

  const submit = async () => {
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      // Scroll to first error using the fresh validation map so we don't
      // race the setValidation state update.
      const firstKey = Object.keys(errs)[0];
      const el = document.querySelector(`[data-field-id="${firstKey}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/forms/${token}/submit`, {
        ...contact, responses, signature,
      });
      setStage("done");
    } catch (err) {
      setErrMsg(err.response?.data?.detail || "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (stage === "loading") {
    return (
      <div className="min-h-screen bg-[#FFF8E7] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }
  if (stage === "error") {
    return (
      <div className="min-h-screen bg-[#FFF8E7] flex items-center justify-center p-6">
        <div className="bg-white border border-slate-200 rounded-2xl p-8 max-w-md w-full text-center">
          <div className="w-14 h-14 rounded-full bg-rose-50 mx-auto flex items-center justify-center mb-4">
            <AlertTriangle className="w-7 h-7 text-rose-700" />
          </div>
          <h1 className="font-['Outfit'] text-2xl font-light text-slate-900">Form unavailable</h1>
          <p className="text-sm text-slate-500 mt-2">{errMsg}</p>
        </div>
      </div>
    );
  }
  if (stage === "done") {
    return (
      <div className="min-h-screen bg-[#FFF8E7] flex items-center justify-center p-6">
        <div className="bg-white border border-slate-200 rounded-2xl p-10 max-w-md w-full text-center" data-testid="public-form-done">
          <div className="w-16 h-16 rounded-full bg-emerald-100 mx-auto flex items-center justify-center mb-4">
            <Check className="w-8 h-8 text-emerald-700" />
          </div>
          <h1 className="font-['Outfit'] text-2xl font-light text-slate-900">All set, thank you!</h1>
          <p className="text-sm text-slate-500 mt-2">
            Your responses have been sent. We&apos;ll be in touch soon.
          </p>
        </div>
      </div>
    );
  }

  // Apply the salon-owner's chosen theme (background + accent for the
  // submit button). Defaults match the cream/dark palette the form used
  // to ship with — owners who never open the Style dialog see no change.
  const theme = form.theme || {};
  const bg = theme.background_color || "#FFF8E7";
  const accent = theme.accent_color || "#0F172A";
  const submitText = form.submit_text || "Submit";

  return (
    <div className="min-h-screen py-10 px-4" style={{ backgroundColor: bg }}>
      <div className="max-w-2xl mx-auto" data-testid="public-form">
        <div className="bg-white border border-slate-200 rounded-3xl p-8 sm:p-10">
          <h1 className="font-['Outfit'] text-3xl font-semibold text-slate-900 tracking-tight">{form.name}</h1>
          {form.description && (
            <p className="text-sm text-slate-500 mt-2 leading-relaxed">{form.description}</p>
          )}

          {/* Contact block — required by the public submit endpoint */}
          <div className="mt-7 pt-6 border-t border-slate-100 space-y-4">
            <p className="text-[11px] uppercase tracking-widest font-bold text-slate-500 font-['Manrope']">Your information</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="First name" required error={validation.__first_name} fieldId="__first_name">
                <input value={contact.first_name} onChange={e => setContact(c => ({ ...c, first_name: e.target.value }))}
                  data-testid="public-first-name"
                  className="w-full h-11 bg-[#FAFAFA] rounded-xl px-3 border border-slate-200 focus:border-slate-400 outline-none text-sm" />
              </Field>
              <Field label="Last name" fieldId="__last_name">
                <input value={contact.last_name} onChange={e => setContact(c => ({ ...c, last_name: e.target.value }))}
                  data-testid="public-last-name"
                  className="w-full h-11 bg-[#FAFAFA] rounded-xl px-3 border border-slate-200 focus:border-slate-400 outline-none text-sm" />
              </Field>
              <Field label="Email" fieldId="__email">
                <input type="email" value={contact.email} onChange={e => setContact(c => ({ ...c, email: e.target.value }))}
                  data-testid="public-email"
                  className="w-full h-11 bg-[#FAFAFA] rounded-xl px-3 border border-slate-200 focus:border-slate-400 outline-none text-sm" />
              </Field>
              <Field label="Phone" fieldId="__phone">
                <input type="tel" value={contact.phone} onChange={e => setContact(c => ({ ...c, phone: e.target.value }))}
                  data-testid="public-phone"
                  className="w-full h-11 bg-[#FAFAFA] rounded-xl px-3 border border-slate-200 focus:border-slate-400 outline-none text-sm" />
              </Field>
            </div>
          </div>

          {/* Form fields rendered in a 2-col grid so half-width fields
              can pair up next to each other on desktop. */}
          <div className="mt-8 pt-6 border-t border-slate-100 grid grid-cols-2 gap-x-3 gap-y-5">
            {(form.fields || []).map(f => (
              <FormField
                key={f.id}
                field={f}
                value={responses[f.id]}
                onChange={v => setResponse(f.id, v)}
                error={validation[f.id]}
              />
            ))}
          </div>

          {/* Signature */}
          <div className="mt-7 pt-6 border-t border-slate-100">
            <Field label="Signature (type your full name)" fieldId="__signature">
              <input value={signature} onChange={e => setSignature(e.target.value)}
                placeholder="Sign by typing your full name..."
                data-testid="public-signature"
                className="w-full h-12 bg-[#FAFAFA] rounded-xl px-3 border border-slate-200 focus:border-slate-400 outline-none italic text-slate-700" />
            </Field>
          </div>

          {errMsg && stage === "form" && (
            <div className="mt-4 bg-rose-50 border border-rose-200 rounded-xl p-3 text-sm text-rose-700">
              {errMsg}
            </div>
          )}

          <button onClick={submit} disabled={submitting}
            data-testid="public-submit"
            style={{ backgroundColor: accent }}
            className="mt-7 w-full h-12 rounded-xl text-white font-semibold text-base disabled:opacity-40 hover:opacity-90 transition-opacity">
            {submitting ? "Submitting…" : submitText}
          </button>
        </div>
        <p className="text-center text-[11px] text-slate-400 mt-4 font-['Manrope']">
          Secured by SalonFlow OS
        </p>
      </div>
    </div>
  );
}

function Field({ label, required, error, fieldId, children }) {
  return (
    <div data-field-id={fieldId}>
      <label className="text-xs font-medium text-slate-600 mb-1.5 block font-['Manrope']">
        {label}{required && <span className="text-rose-600 ml-0.5">*</span>}
      </label>
      {children}
      {error && <p className="text-[11px] text-rose-600 mt-1">{error}</p>}
    </div>
  );
}

// One renderer for every field type the editor supports. Keep this in
// sync with `FormsPage.js` FIELD_TYPES.
function FormField({ field, value, onChange, error }) {
  const t = field.type;
  // Half-width fields share a row on desktop; everything else takes the
  // full 2-column span. Layout fields are always full-width.
  const spanFull = (field.width || "full") === "full" || ["heading", "paragraph", "divider", "signature", "textarea", "address"].includes(t);
  const colClass = spanFull ? "col-span-2" : "col-span-2 sm:col-span-1";

  if (t === "heading") {
    return (
      <h3 className={`font-['Outfit'] text-lg font-semibold text-slate-900 pt-3 ${colClass}`} data-field-id={field.id}>
        {field.label}
      </h3>
    );
  }
  if (t === "paragraph") {
    return <p className={`text-sm text-slate-600 leading-relaxed ${colClass}`} data-field-id={field.id}>{field.label}</p>;
  }
  if (t === "divider") {
    return <hr className={`border-slate-100 ${colClass}`} data-field-id={field.id} />;
  }

  return (
    <div className={colClass} data-field-id={field.id}>
      <label className="text-xs font-medium text-slate-600 mb-1.5 block font-['Manrope']">
        {field.label}{field.required && <span className="text-rose-600 ml-0.5">*</span>}
      </label>
      {field.help_text && <p className="text-[11px] text-slate-400 -mt-1 mb-1.5">{field.help_text}</p>}
      {renderInput(t, field, value, onChange)}
      {error && <p className="text-[11px] text-rose-600 mt-1">{error}</p>}
    </div>
  );
}

function renderInput(t, field, value, onChange) {
  const baseInput = "w-full h-11 bg-[#FAFAFA] rounded-xl px-3 border border-slate-200 focus:border-slate-400 outline-none text-sm";
  const placeholder = field.placeholder || "";

  if (t === "textarea") {
    return <textarea value={value || ""} onChange={e => onChange(e.target.value)}
      placeholder={placeholder} rows={4}
      className="w-full bg-[#FAFAFA] rounded-xl px-3 py-2 border border-slate-200 focus:border-slate-400 outline-none text-sm resize-none" />;
  }
  if (t === "email") return <input type="email" value={value || ""} onChange={e => onChange(e.target.value)} placeholder={placeholder} className={baseInput} />;
  if (t === "phone") return <input type="tel" value={value || ""} onChange={e => onChange(e.target.value)} placeholder={placeholder} className={baseInput} />;
  if (t === "url") return <input type="url" value={value || ""} onChange={e => onChange(e.target.value)} placeholder={placeholder || "https://"} className={baseInput} />;
  if (t === "number") return <input type="number" value={value ?? ""} onChange={e => onChange(e.target.value)} placeholder={placeholder} className={baseInput} />;
  if (t === "date") return <input type="date" value={value || ""} onChange={e => onChange(e.target.value)} className={baseInput} />;
  if (t === "time") return <input type="time" value={value || ""} onChange={e => onChange(e.target.value)} className={baseInput} />;
  if (t === "address") return <textarea value={value || ""} onChange={e => onChange(e.target.value)}
    placeholder={placeholder || "Street, City, State ZIP"} rows={2}
    className="w-full bg-[#FAFAFA] rounded-xl px-3 py-2 border border-slate-200 focus:border-slate-400 outline-none text-sm resize-none" />;

  if (t === "dropdown") {
    return (
      <div className="relative">
        <select value={value || ""} onChange={e => onChange(e.target.value)}
          className={`${baseInput} appearance-none pr-9`}>
          <option value="">Select…</option>
          {(field.options || []).map((o, i) => <option key={i} value={o}>{o}</option>)}
        </select>
        <ChevronDown className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
      </div>
    );
  }
  if (t === "radio") {
    return (
      <div className="space-y-2">
        {(field.options || []).map((o, i) => (
          <label key={i} className="flex items-center gap-2 cursor-pointer">
            <input type="radio" name={field.id} value={o} checked={value === o}
              onChange={() => onChange(o)} className="accent-slate-900" />
            <span className="text-sm text-slate-700">{o}</span>
          </label>
        ))}
      </div>
    );
  }
  if (t === "checkbox") {
    return (
      <label className="flex items-center gap-2 cursor-pointer">
        <input type="checkbox" checked={!!value} onChange={e => onChange(e.target.checked)}
          className="w-4 h-4 accent-slate-900" />
        <span className="text-sm text-slate-700">{placeholder || field.label}</span>
      </label>
    );
  }
  if (t === "multi_select") {
    const arr = Array.isArray(value) ? value : [];
    const toggle = (o) =>
      onChange(arr.includes(o) ? arr.filter(x => x !== o) : [...arr, o]);
    return (
      <div className="space-y-2">
        {(field.options || []).map((o, i) => (
          <label key={i} className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={arr.includes(o)} onChange={() => toggle(o)}
              className="w-4 h-4 accent-slate-900" />
            <span className="text-sm text-slate-700">{o}</span>
          </label>
        ))}
      </div>
    );
  }
  if (t === "yes_no") {
    return (
      <div className="grid grid-cols-2 gap-2">
        {["Yes", "No"].map(opt => (
          <button key={opt} type="button" onClick={() => onChange(opt)}
            className={`h-11 rounded-xl border-2 text-sm font-semibold transition-all ${
              value === opt
                ? (opt === "Yes" ? "bg-emerald-50 border-emerald-500 text-emerald-700" : "bg-rose-50 border-rose-500 text-rose-700")
                : "bg-white border-slate-200 text-slate-600"
            }`}>
            {opt}
          </button>
        ))}
      </div>
    );
  }
  if (t === "rating") {
    const max = field.max || 5;
    const v = Number(value) || 0;
    return (
      <div className="flex items-center gap-1.5">
        {Array.from({ length: max }).map((_, i) => (
          <button key={i} type="button" onClick={() => onChange(i + 1)}
            className="text-amber-400 hover:scale-110 transition-transform">
            <Star className="w-7 h-7" fill={i < v ? "#F59E0B" : "transparent"} strokeWidth={1.6} />
          </button>
        ))}
        <span className="ml-2 text-xs text-slate-400">{v || 0} / {max}</span>
      </div>
    );
  }
  if (t === "signature") {
    return <SignaturePad value={value} onChange={onChange} />;
  }
  // Fallback: short text
  return <input type="text" value={value || ""} onChange={e => onChange(e.target.value)} placeholder={placeholder} className={baseInput} />;
}

// Simple typed signature pad — keeps the field type consistent with the
// editor's "Signature" option without needing canvas drawing on touch.
function SignaturePad({ value, onChange }) {
  return (
    <div className="bg-[#FAFAFA] border border-slate-200 rounded-xl p-3">
      <input value={value || ""} onChange={e => onChange(e.target.value)}
        placeholder="Sign by typing your full name…"
        className="w-full h-10 bg-transparent border-0 outline-none italic text-slate-700 text-base" />
      <p className="text-[10px] text-slate-400 mt-1">By typing your name above you agree this is your electronic signature.</p>
    </div>
  );
}
